import { waitForProjectServices } from '../hooks/useProjectServices';
import { serviceState } from '../services/SandboxService';
import { isPreviewReady, isStoppedOnPurpose, serviceEntryFor } from './previewReadiness';

/**
 * Where a service is when the Preview opens it, from the shared store, WAITING for the store's first answer. On a
 * cold page load the store has no answer yet (its first calls can even fail while the session settles), and "not
 * known yet" used to be read as "no session": the Preview posted /api/render and started an app the user had
 * stopped. Every Preview path that may render on its own (tab creation, the loader, the auto-render after a failed
 * iframe load or files landing) asks this first.
 *
 * Resolves to `{ state, entry }`, `state` one of ready | stopped | building | running | crashed | failed | absent
 * (the runner has no session), or null when vfs-service cannot tell (then the caller does what it did before).
 * @param {string} projectId
 * @param {string} serviceName
 * @param {{ maxAgeMs?: number, timeoutMs?: number, retryMs?: number }} [options] `maxAgeMs` 0: always a fresh answer
 */
export async function previewRunStateFor(projectId, serviceName, options = {}) {
  const answer = await waitForProjectServices(projectId, options).catch(() => undefined);
  if (!answer || answer.servicesUnavailable) { return { state: null, entry: null }; }
  const entry = serviceEntryFor(answer.services, serviceName);
  if (!entry) { return { state: 'absent', entry: null }; }
  if (isStoppedOnPurpose(entry)) { return { state: 'stopped', entry }; }
  return { state: isPreviewReady(entry) ? 'ready' : serviceState(entry), entry };
}

/**
 * What Canvas's preview loader (its `[service, project, revision, force, activeTab]` effect) does when it opens a
 * service, decided from the store's real answer (previewRunStateFor, always fresh):
 * - `stopped`: show the stopped state, never render, whatever else holds (a forced fetch, a "pending initial prompt"
 *   left in sessionStorage, which survives a reload of the tab, no tab or entry yet);
 * - `show`: already running and ready, show it without a render (not for a forced fetch or a Home-created project,
 *   which render as they always did);
 * - `follow`: already building or starting (a render posted a moment ago, by this Preview, the agent or Start):
 *   follow that build until it is ready, never post a second render (the loader re-runs, e.g. when its `force`
 *   flag flips back, and used to post again a second after the first);
 * - `render`: no session (never run, reaped, or a sandbox that woke up: a stop does not survive that), a failed or
 *   crashed one, or no answer within the timeout: render as before.
 * @param {{ projectId: string, serviceName: string, force?: boolean, pendingInitialPrompt?: boolean,
 *   timeoutMs?: number, retryMs?: number }} input
 * @returns {Promise<{ action: 'stopped'|'show'|'follow'|'render', state: (string|null), entry: (object|null) }>}
 */
export async function decidePreviewOpen({ projectId, serviceName, force = false, pendingInitialPrompt = false, timeoutMs, retryMs }) {
  const { state, entry } = await previewRunStateFor(projectId, serviceName, { maxAgeMs: 0, timeoutMs, retryMs });
  if (state === 'stopped') { return { action: 'stopped', state, entry }; }
  if (state === 'ready' && !force && !pendingInitialPrompt) { return { action: 'show', state, entry }; }
  if (state === 'building' || state === 'running') { return { action: 'follow', state, entry }; }
  return { action: 'render', state, entry };
}

export default previewRunStateFor;
